import { driverLicenseExampleValues, zDriverLicense } from '@/general/src/insuranceDoc/driverLicense/shared.js'
import { eptsExampleValues, zEpts } from '@/general/src/insuranceDoc/epts/shared.js'
import { passportExampleValues, zPassportWithUploads } from '@/general/src/insuranceDoc/passport/shared.js'
import { ptsExampleValues, zPts } from '@/general/src/insuranceDoc/pts/shared.js'
import { stsExampleValues, zSts } from '@/general/src/insuranceDoc/sts/shared.js'
import type { InsuranceTypeGeneral } from '@/general/src/insuranceType/shared.js'
import { zDateString } from '@/general/src/other/date.js'
import { zPhoneRequired } from '@/general/src/other/phone.js'
import { z } from 'zod'

// "Выбор из:
// Личные
// Учебная езда
// Инкасация
// Скорая помощь
// Такси
// Дорожные и спецаильные ТС
// Прочие
// Регулярные/по заказам пассажирские перевозки
// Перевозка опасных и легковоспламеняемых грузров
// Экстренные и коммунальные службы
// Прокат/краткосрочная аренда"
export const zVehicleUsagePurpose = z.enum([
  'personal',
  'training',
  'incasation',
  'emergency',
  'taxi',
  'special',
  'other',
  'regular',
  'dangerous',
  'extraneous',
  'rental',
])
export const vehicleUsagePurposeKeysMap: Record<z.infer<typeof zVehicleUsagePurpose>, string> = {
  personal: 'Личные',
  training: 'Учебная езда',
  incasation: 'Инкасация',
  emergency: 'Скорая помощь',
  taxi: 'Такси',
  special: 'Дорожные и спецаильные ТС',
  other: 'Прочие',
  regular: 'Регулярные/по заказам пассажирские перевозки',
  dangerous: 'Перевозка опасных и легковоспламеняемых грузров',
  extraneous: 'Экстренные и коммунальные службы',
  rental: 'Прокат/краткосрочная аренда',
}

export const zPersonalOsagoInput = z
  .object({
    stsOrPtsOrEpts: z.enum(['sts', 'pts', 'epts']),
    policyholderPhone: zPhoneRequired,
    policyholderIsCarOwner: z.boolean(),
    policyholderPassport: zPassportWithUploads,
    carOwnerPassport: zPassportWithUploads.nullable().optional(),
    driversLicenses: z.array(zDriverLicense).min(1, { message: 'Хотя бы одни водительские права обязательны' }),
    pts: zPts.nullable().optional(),
    epts: zEpts.nullable().optional(),
    sts: zSts.nullable().optional(),
    insuranceStartDate: zDateString,
    applicationIssueDate: zDateString,
    isTrailerUsed: z.boolean(),
    vehicleUsagePurpose: zVehicleUsagePurpose,
    insuranceAccessType: z.enum(['limited', 'unlimited']),
  })
  .superRefine((data, ctx) => {
    if (data.stsOrPtsOrEpts === 'pts' && !data.pts) {
      ctx.addIssue({
        path: ['pts'],
        code: z.ZodIssueCode.custom,
        message: 'ПТС обязателен',
      })
    }
    if (data.stsOrPtsOrEpts === 'sts' && !data.sts) {
      ctx.addIssue({
        path: ['sts'],
        code: z.ZodIssueCode.custom,
        message: 'СТС обязателен',
      })
    }
    if (data.stsOrPtsOrEpts === 'epts' && !data.epts) {
      ctx.addIssue({
        path: ['epts'],
        code: z.ZodIssueCode.custom,
        message: 'Электронный ПТС обязателен',
      })
    }
    if (!data.policyholderIsCarOwner && !data.carOwnerPassport) {
      ctx.addIssue({
        path: ['carOwnerPassport'],
        code: z.ZodIssueCode.custom,
        message: 'Пасспорт собственника автомобиля обязателен',
      })
    }
  })

export type PersonalOsagoInput = z.output<typeof zPersonalOsagoInput>
export const personalOsagoInputExampleValues: PersonalOsagoInput = {
  stsOrPtsOrEpts: 'sts',
  policyholderPhone: '+79090099900',
  policyholderIsCarOwner: true,
  policyholderPassport: passportExampleValues,
  carOwnerPassport: passportExampleValues,
  driversLicenses: [driverLicenseExampleValues],
  sts: stsExampleValues,
  pts: ptsExampleValues,
  epts: eptsExampleValues,
  applicationIssueDate: '01.01.2025',
  insuranceStartDate: '01.01.2025',
  isTrailerUsed: false,
  vehicleUsagePurpose: 'personal',
  insuranceAccessType: 'limited',
}

export const personalOsagoGeneral = {
  title: 'ОСАГО физ. лица',
  insuranceType: 'personalOsago',
  insuranceCustomerType: 'personal',
  zInput: zPersonalOsagoInput,
  exampleValues: personalOsagoInputExampleValues,
  insuranceScenariosKeys: ['personalOsagoAlfa', 'personalOsagoIngos'],
} satisfies InsuranceTypeGeneral
